// Client layer (TS-2.5.03). Detail sections grouped by team-group, mirroring the recovered
// reference's card area (decomposition.md section 8.7 point 8/9). Groups are already ordered and
// already narrowed by the server for both filter axes (area = Hide, status-on-cards = Hide -
// D-199), so this component's only remaining job is HideGroupWhenNoVisibleCards as a defensive
// client-side backstop - it is semantics data, never a hardcoded rule.
import { ProjectDetailCard } from "./ProjectDetailCard";
import { ownerInfoFor } from "./lib/resolveProjectOwners";
import type { ProjectOwnerInfo } from "./lib/resolveProjectOwners";
import type { GlanceGroup } from "../../api/generated/contracts";

export interface DetailSectionsProps {
  readonly groups: readonly GlanceGroup[];
  readonly hideEmptyGroups: boolean;
  // D-280: the projectKey -> {productName, businessOwner} join every card resolves its Lead/
  // Business owner/Product line from - optional so this component (and any existing caller/test)
  // keeps working, degraded to "no owner info", before the join has resolved.
  readonly ownerInfoByProjectKey?: ReadonlyMap<string, ProjectOwnerInfo>;
}

export function DetailSections({ groups, hideEmptyGroups, ownerInfoByProjectKey }: DetailSectionsProps) {
  const visible = hideEmptyGroups ? groups.filter((group) => group.rows.length > 0) : groups;

  if (visible.length === 0) {
    return <p className="eg-detail-empty-state">No project detail matches the current filters.</p>;
  }

  return (
    <div className="eg-detail-sections">
      {visible.map((group) => (
        <section key={group.title} aria-labelledby={`eg-group-${group.title}`}>
          <h2 id={`eg-group-${group.title}`}>{group.title}</h2>
          <div className="eg-detail-sections__cards">
            {group.rows.map((row) => (
              <ProjectDetailCard
                key={row.key}
                project={row}
                ownerInfo={ownerInfoByProjectKey ? ownerInfoFor(ownerInfoByProjectKey, row.key) : undefined}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
